import type { ClientLink } from './client.ts';
import type { HostSession, PeerHandle } from './host.ts';

export interface InMemoryClientLink extends ClientLink {
  /** Simulate the connection dropping. */
  close(): void;
  readonly closed: boolean;
  /** Every raw host→client frame, for leak audits in tests. */
  readonly received: readonly string[];
}

/**
 * Synchronous in-memory transport (FR-NET-01). Used by tests and hot-seat play,
 * where host and every client run in the same JS runtime.
 */
export function connectInMemory(host: HostSession, identity?: { readonly account?: string }): InMemoryClientLink {
  const listeners: ((d: string) => void)[] = [];
  const received: string[] = [];
  let closed = false;
  const handle: PeerHandle = host.connect({
    send(data) {
      if (closed) return;
      received.push(data);
      for (const l of listeners) l(data);
    },
    // The host hung up (e.g. the player was removed).
    close() {
      closed = true;
    },
  }, identity);
  return {
    send(data) {
      if (!closed) handle.receive(data);
    },
    onMessage(cb) {
      listeners.push(cb);
    },
    close() {
      if (closed) return;
      closed = true;
      handle.disconnect();
    },
    get closed() {
      return closed;
    },
    received,
  };
}
